import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import Paper from "@mui/material/Paper";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import Table from "@mui/material/Table";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TableCell from "@mui/material/TableCell";
import TableBody from "@mui/material/TableBody";
import Chip from "@mui/material/Chip";
import Button from "@mui/material/Button";
import Stack from "@mui/material/Stack";
import AddIcon from "@mui/icons-material/Add";
import { PageHeader } from "@/components/common/PageHeader";
import { KpiCard } from "@/components/common/KpiCard";
import { QueryState } from "@/components/common/AsyncState";
import { StatusChip } from "@/components/common/StatusChip";
import { useAuth } from "@/auth/useAuth";
import { approveTeacher, fetchPendingTeachers, fetchTeachers, rejectTeacher } from "@/api/teachers";
import { TEACHER_STATUS_LABELS } from "@/types/enums";
import { formatMoney, formatPercent } from "@/utils/format";
import { TeacherFormDialog } from "./TeacherFormDialog";
import type { Teacher } from "@/types/teacher";

export function TeachersListPage() {
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  const canApprove = hasPermission("teachers.approve");
  const canEdit = hasPermission("teachers.edit");
  const showSalary = hasPermission("fin.salary");
  const [tab, setTab] = useState(0);
  const [formTarget, setFormTarget] = useState<Teacher | "new" | null>(null);
  const queryClient = useQueryClient();

  const { data, isLoading, error } = useQuery({
    queryKey: ["teachers", "active"],
    queryFn: () => fetchTeachers({ status: "ACTIVE", limit: 200 }),
  });

  const { data: pending, isLoading: pendingLoading } = useQuery({
    queryKey: ["teachers", "pending"],
    queryFn: fetchPendingTeachers,
    enabled: canApprove,
  });

  const approveMutation = useMutation({
    mutationFn: (id: string) => approveTeacher(id),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ["teachers"] }),
  });
  const rejectMutation = useMutation({
    mutationFn: (id: string) => rejectTeacher(id),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ["teachers"] }),
  });

  const active = data?.items ?? [];
  const pendingCount = pending?.length ?? 0;
  const groupsLed = active.reduce((sum, t) => sum + (t.groupsCount ?? 0), 0);
  const avgPercent = active.length ? Math.round(active.reduce((sum, t) => sum + t.defaultRevenueSharePercent, 0) / active.length) : 0;
  const payrollFund = active.reduce((sum, t) => sum + (t.monthToPay ?? 0), 0);

  return (
    <>
      <PageHeader
        title="Преподаватели"
        subtitle="Список преподавателей и заявки на регистрацию"
        actions={
          canEdit && (
            <Button variant="contained" startIcon={<AddIcon />} onClick={() => setFormTarget("new")}>
              Добавить преподавателя
            </Button>
          )
        }
      />

      <Grid container spacing={2} mb={2}>
        <Grid item xs={12} sm={6} md={3}>
          <KpiCard label="Преподавателей" value={String(active.length)} hint={pendingCount ? `${pendingCount} ждут подтверждения` : "все подтверждены"} />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <KpiCard label="Групп ведут" value={String(groupsLed)} />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <KpiCard label="Средний процент" value={`${avgPercent}%`} hint="от прихода своих групп" />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          {showSalary ? (
            <KpiCard label="Фонд зарплат за месяц" value={formatMoney(payrollFund)} />
          ) : (
            <KpiCard label="Филиалов" value={String(new Set(active.map((t) => t.branchId)).size)} />
          )}
        </Grid>
      </Grid>

      <Paper className="crm-data-panel" variant="outlined">
        <Tabs value={tab} onChange={(_, v) => setTab(v)} variant="scrollable" scrollButtons={false}>
          <Tab label="Все преподаватели" />
          {canApprove && (
            <Tab
              label={
                <Box display="flex" alignItems="center" gap={1}>
                  Заявки на подтверждение
                  {Boolean(pending?.length) && <Chip size="small" color="warning" label={pending?.length} />}
                </Box>
              }
            />
          )}
        </Tabs>
        <Box>
          {tab === 0 && (
            <QueryState isLoading={isLoading} error={error}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>ФИО</TableCell>
                    <TableCell>Телефон</TableCell>
                    <TableCell>Филиал</TableCell>
                    <TableCell>Предметы</TableCell>
                    <TableCell align="right">Групп</TableCell>
                    <TableCell align="right">Учеников</TableCell>
                    <TableCell align="right">Посещ.</TableCell>
                    <TableCell align="right">Балл</TableCell>
                    <TableCell align="right">%</TableCell>
                    {showSalary && (
                      <>
                        <TableCell align="right">Приход за месяц</TableCell>
                        <TableCell align="right">К выплате</TableCell>
                      </>
                    )}
                    {canEdit && <TableCell align="right">Действие</TableCell>}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {active.map((teacher) => (
                    <TableRow key={teacher.id} hover sx={{ cursor: "pointer" }} onClick={() => navigate(`/teachers/${teacher.id}`)}>
                      <TableCell>{teacher.fullName}</TableCell>
                      <TableCell>{teacher.phone}</TableCell>
                      <TableCell>{teacher.branchName ?? "—"}</TableCell>
                      <TableCell>{(teacher.subjectNames ?? []).join(", ") || "—"}</TableCell>
                      <TableCell align="right">{teacher.groupsCount ?? "—"}</TableCell>
                      <TableCell align="right">{teacher.studentsCount ?? "—"}</TableCell>
                      <TableCell align="right">{teacher.attendancePercent != null ? `${teacher.attendancePercent}%` : "—"}</TableCell>
                      <TableCell align="right">{teacher.averageGrade ?? "—"}</TableCell>
                      <TableCell align="right">
                        <b>{formatPercent(teacher.defaultRevenueSharePercent)}</b>
                      </TableCell>
                      {showSalary && (
                        <>
                          <TableCell align="right">{formatMoney(teacher.monthRevenue ?? 0)}</TableCell>
                          <TableCell align="right">{formatMoney(teacher.monthToPay ?? 0)}</TableCell>
                        </>
                      )}
                      {canEdit && (
                        <TableCell align="right" onClick={(e) => e.stopPropagation()}>
                          <Button size="small" onClick={() => setFormTarget(teacher)}>
                            Изменить
                          </Button>
                        </TableCell>
                      )}
                    </TableRow>
                  ))}
                  {active.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={showSalary ? 11 : 9} align="center">
                        Преподаватели не найдены.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </QueryState>
          )}

          {tab === 1 && canApprove && (
            <QueryState isLoading={pendingLoading} error={null}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>ФИО</TableCell>
                    <TableCell>Телефон</TableCell>
                    <TableCell>Филиал</TableCell>
                    <TableCell>Предметы</TableCell>
                    <TableCell>Статус</TableCell>
                    <TableCell align="right">Действие</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(pending ?? []).map((teacher) => (
                    <TableRow key={teacher.id}>
                      <TableCell>{teacher.fullName}</TableCell>
                      <TableCell>{teacher.phone}</TableCell>
                      <TableCell>{teacher.branchName ?? "—"}</TableCell>
                      <TableCell>{(teacher.subjectNames ?? []).join(", ") || "—"}</TableCell>
                      <TableCell>
                        <StatusChip label={TEACHER_STATUS_LABELS[teacher.status]} />
                      </TableCell>
                      <TableCell align="right">
                        <Stack direction="row" spacing={1} justifyContent="flex-end">
                          <Button
                            size="small"
                            variant="contained"
                            disabled={approveMutation.isPending}
                            onClick={() => approveMutation.mutate(teacher.id)}
                          >
                            Подтвердить
                          </Button>
                          <Button
                            size="small"
                            color="error"
                            disabled={rejectMutation.isPending}
                            onClick={() => rejectMutation.mutate(teacher.id)}
                          >
                            Отклонить
                          </Button>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  ))}
                  {(pending ?? []).length === 0 && (
                    <TableRow>
                      <TableCell colSpan={6} align="center">
                        Новых заявок нет.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </QueryState>
          )}
        </Box>
      </Paper>

      {canEdit && (
        <TeacherFormDialog
          open={formTarget !== null}
          onClose={() => setFormTarget(null)}
          teacher={formTarget === "new" || formTarget === null ? null : formTarget}
        />
      )}
    </>
  );
}
